import { useCallback, useState } from 'react';
import type { UserEntity } from '@user/UserEntity';
import { ExternalLink, FlatButton } from '@framework/ui/atoms';
import { Loading } from '@framework/ui/atoms/Loading';
import { Modal } from '@framework/ui/elements';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onClose(): void;
  userEntity: UserEntity;
};

/**
 * Firebase Auth の linkWithRedirect でログイン中ユーザーに Google アカウントを連携するモーダル。
 *
 * リダイレクト復帰後の検証(メールアドレス一致)と結果表示は GoogleLinkRedirectResultHandler が行う。
 */
export const ConnectGoogleModal: React.FC<Props> = ({ isOpen, onClose, userEntity }: Props) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);

  const handleConnectWithGoogle = useCallback(async () => {
    setProcessing(true);

    try {
      // 成功時はページが redirect で離脱するため processing は戻さない。
      await FirebaseAuthenticationClient.linkWithRedirectToGoogle();
    } catch (error) {
      console.error(error);
      toast.error(t('auth.googleLink.result.error'));
      setProcessing(false);
    }
  }, [t]);

  const handleClose = useCallback(() => {
    setProcessing(false);
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="flex flex-col">
      {/* header */}
      <div className="px-8 pb-4 pt-8 text-2xl font-bold">{t('auth.googleConnect.modal.title')}</div>
      <hr className="w-full" />

      {/* content */}
      <div className="flex flex-col items-center p-8">
        {processing ? (
          <Loading />
        ) : (
          <>
            <div className="pb-4">
              <Trans
                i18nKey="auth.googleConnect.modal.description"
                values={{ email: userEntity.mailAddress }}
                components={{ emailSpan: <span className="px-2" /> }}
              />
              <div className="pt-1 text-xs">
                <Trans
                  i18nKey="auth.googleConnect.modal.emailChangeNote"
                  components={{
                    contactFormLink: <ExternalLink href="https://balus-help.levii.co.jp/hc/ja/requests/new" />,
                  }}
                />
              </div>
            </div>

            <div className="flex w-80 flex-col space-y-4 px-5 py-2">
              <FlatButton color="brand" onClick={handleConnectWithGoogle}>
                <span className="pr-2 text-lg">
                  <FontAwesomeIcon icon={faGoogle} />
                </span>
                {t('auth.login.loginWithGoogle')}
              </FlatButton>
            </div>
          </>
        )}
      </div>

      {/* footer */}
      <hr className="w-full" />
      <div className="flex justify-end px-8 pb-8 pt-4">
        <FlatButton onClick={handleClose}>{t('common.action.close')}</FlatButton>
      </div>
    </Modal>
  );
};
